
/**
 * --- External Imports ---
 */
import * as Sentry from '@sentry/react';
import { FC, useEffect, useMemo } from 'react';

/**
 * --- Internal Imports ---
 */
import { AuthenticateResponse } from '@/apis/dtos';
import Loading from '@/components/layout/Loading';
import AuthContext from '@/contexts/auth/AuthContext';
import { createSentryAnonymousUser } from '@/errors/error';
import { useAuthenticatedUser } from '@/hooks/auth/useAuthenticatedUser';
import IHasChildren from '@/types/components/IHasChildren';

declare let global: any;

if (typeof global === 'undefined') {
    (window as any).global = window;
}

interface IAuthProviderProps extends IHasChildren {
    initialValue: AuthenticateResponse;
}

const AuthProvider: FC<IAuthProviderProps> = ({
    children,
    initialValue
}) => {
    const authenticatedUser = useAuthenticatedUser({ errorRetryCount: 0, refreshInterval: 600000, fallbackData: initialValue });

    useEffect(() => {
        if (!authenticatedUser.data?.userName && !authenticatedUser.loading) {
            global.location.href = `/auth/openiddict?redirect=${encodeURIComponent(global.location.pathname + global.location.search)}`;
        }
    }, [authenticatedUser.data, authenticatedUser.error, authenticatedUser.loading])

    // Keep the Sentry user in sync with the authenticated user so errors and
    // traces are attributed to the correct user. When there is no user the
    // Sentry user is reset back to anonymous.
    useEffect(() => {
        // no user, clear sentry user back to anonymous.
        if (!authenticatedUser.data) {
            Sentry.setUser(createSentryAnonymousUser());
            return;
        }
        // user, set sentry user.
        Sentry.setUser({
            id: authenticatedUser.data.userName,
            username: authenticatedUser.data.userName,
            fullName: authenticatedUser.data.displayName,
            authenticated: true
        })
        return () => {
            // set Sentry user back to anonymous on unmount / user change.
            Sentry.setUser(createSentryAnonymousUser());
        }
    }, [authenticatedUser.data])

    const context = useMemo(() => {
        return authenticatedUser;
    }, [authenticatedUser]);

    return (
        <AuthContext.Provider value={context}>
            <Loading 
                name="authenticatedUser" 
                isLoading={authenticatedUser.loading} 
                isValidating={authenticatedUser.validating} 
            />
            {
                !authenticatedUser.loading && authenticatedUser.data && children
            }
        </AuthContext.Provider>
    )
}

export default AuthProvider

